// Internet Plan Modal - Self-contained modal for displaying internet plan details

import { useEffect } from 'react'
import type { InternetPlan } from '@/types'
import {
  getScoreColor,
  formatSpeed,
  getScoreMessaging,
  getCapabilities,
  getCapabilityColor,
  getLatencyLevel,
  getLatencyColor,
} from '@/utils/planCalculations'

interface InternetPlanModalProps {
  plan: InternetPlan
  onClose: () => void
  onSelect: () => void
  isSelected: boolean
}

const capLabels: Record<string, string> = {
  streaming: 'Streaming',
  multidevice: 'Multi-device',
  gaming: 'Gaming',
  wholehome: 'Whole home',
}

export function InternetPlanModal({
  plan,
  onClose,
  onSelect,
  isSelected,
}: InternetPlanModalProps) {
  const scoreColor = getScoreColor(plan.percentileScore)
  const scoreMessage = getScoreMessaging(plan.percentileScore)
  const capabilities = getCapabilities(plan.technology)
  const latencyLevel = getLatencyLevel(plan.technology)
  const latencyColor = getLatencyColor(latencyLevel)

  // Handle overlay click (close modal)
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  return (
    <div className="modal modal--active">
      <div className="modal__overlay" onClick={handleOverlayClick}></div>
      <div className="modal__container internet-modal">
        {/* Header */}
        <div className="modal__header">
          <h2 className="modal__title">
            {plan.vendorName} - {formatSpeed(plan.downloadSpeed)} Internet
          </h2>
          <button className="modal__close" onClick={onClose} aria-label="Close modal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal__body">
          {/* Logo */}
          <div className="internet-modal__logo">
            <img
              src={plan.logo || `/img/provider-logo_${plan.vendorName.toLowerCase().replace(/[^a-z]/g, '')}.png`}
              alt={plan.vendorName}
              className="internet-modal__logo-img"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          </div>

          {/* Two-column content */}
          <div className="internet-modal__content">
            {/* Left Column - Score & Metrics */}
            <div className="internet-modal__column">
              <div className="internet-modal__section">
                <div className="internet-modal__section-header">
                  <h6 className="internet-modal__section-title">Speed Score</h6>
                </div>

                {/* Speed Score Circle */}
                {plan.percentileScore != null && (
                  <div className="internet-modal__speed-score">
                    <div
                      className="internet-modal__score-circle"
                      style={{ borderColor: scoreColor }}
                    >
                      <span className="internet-modal__score-value">
                        {plan.percentileScore.toFixed(0)}
                      </span>
                    </div>
                    <div className="internet-modal__score-content">
                      <p className="internet-modal__score-description">{scoreMessage}</p>
                    </div>
                  </div>
                )}

                {/* Speed Metrics */}
                <div className="internet-modal__speed-metrics">
                  <div className="internet-modal__speed-metric">
                    <span className="internet-modal__metric-label">Avg. Download Speeds</span>
                    <span className="internet-modal__metric-value">
                      <span
                        className="internet-modal__metric-dot"
                        style={{ backgroundColor: scoreColor }}
                      ></span>
                      {formatSpeed(plan.downloadSpeed)}
                    </span>
                  </div>
                  <div className="internet-modal__speed-metric">
                    <span className="internet-modal__metric-label">Avg. Upload Speeds</span>
                    <span className="internet-modal__metric-value">
                      <span
                        className="internet-modal__metric-dot"
                        style={{ backgroundColor: scoreColor }}
                      ></span>
                      {formatSpeed(plan.uploadSpeed)}
                    </span>
                  </div>
                  <div className="internet-modal__speed-metric">
                    <span className="internet-modal__metric-label">Avg. Latency</span>
                    <span className="internet-modal__metric-value">
                      <span
                        className="internet-modal__metric-dot"
                        style={{ backgroundColor: latencyColor }}
                      ></span>
                      {latencyLevel}
                    </span>
                  </div>
                </div>

                <p className="internet-modal__disclaimer">
                  Speed scores are based on real-world data showing average speeds in your area.
                </p>
              </div>
            </div>

            {/* Right Column - Capabilities */}
            <div className="internet-modal__column">
              <div className="internet-modal__section">
                <div className="internet-modal__section-header">
                  <h6 className="internet-modal__section-title">Est. Capabilities</h6>
                </div>

                <div className="internet-modal__capabilities">
                  {(['streaming', 'multidevice', 'gaming', 'wholehome'] as const).map((cap) => {
                    const rating = capabilities[cap]
                    const capColor = getCapabilityColor(rating)
                    const ratingClass = rating.toLowerCase()

                    return (
                      <div key={cap} className="internet-modal__capability">
                        <div className="internet-modal__capability-icon">
                          <img
                            src={`/img/icon/icon_capability-${cap}.svg`}
                            alt={capLabels[cap]}
                          />
                          <span
                            className={`internet-modal__capability-badge internet-modal__capability-badge--${ratingClass}`}
                          ></span>
                        </div>
                        <h6 className="internet-modal__capability-name">{capLabels[cap]}</h6>
                        <p className="internet-modal__capability-rating">
                          <span
                            className="internet-modal__capability-dot"
                            style={{ backgroundColor: capColor }}
                          ></span>
                          {rating}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal__footer">
          <button
            type="button"
            className={`btn ${isSelected ? 'btn--outline' : ''}`}
            onClick={onSelect}
            style={{ width: '100%' }}
          >
            {isSelected ? 'Selected' : 'Select This Plan'}
          </button>
        </div>
      </div>
    </div>
  )
}
